"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import { ChevronDown, Star } from "lucide-react";
import type { FUSportsLivescoreCompetitionGroup, SearchEntityResult } from "fansunited-data-layer";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useLabels } from "@/lib/labels/LabelsProvider";
import type { MatchOddsMap } from "@/features/odds/types";
import type { ResolvedListings } from "@/features/tv-listings/utils/resolveMatchListings";
import { LivescoreMatchRow } from "./LivescoreMatchRow";
import type { LivescoreSport, LivescoreFilter } from "@/views/livescore/utils/livescore";
import { asCompetition } from "@/views/livescore/utils/livescore";

/** Competition pages (standings, overview, etc.) exist only for football today. */
const SPORTS_WITH_COMPETITION_PAGE = new Set<LivescoreSport>(["football"]);

/** Same test as the rows (`LivescoreMatchRow`): the livescore API reports in-play as `LIVE`. */
const LIVE_STATUS_TYPE = "LIVE";
interface LivescoreMatchGroupProps {
  group: FUSportsLivescoreCompetitionGroup;
  entities: Record<string, SearchEntityResult>;
  sport: LivescoreSport;
  filter: LivescoreFilter;
  oddsMap: MatchOddsMap;
  isMobile: boolean;
  isFavourite?: boolean;
  onToggleFavourite?: (competitionId: string) => void;
  /** Map of match ID → pre-resolved broadcaster listings (from the by-date fetch). */
  tvByMatch?: Record<string, ResolvedListings>;
  /** When true, every row reserves the TV icon slot (even without broadcasters) for alignment. */
  reserveTvSlot?: boolean;
  /**
   * Collapsed groups render the header only — name, country, match and live counts — and
   * open on click. This is how the long tail of a day stays cheap: a collapsed group's teams
   * are never fetched. `onToggle` is required whenever `collapsed` is set either way.
   */
  collapsed?: boolean;
  onToggle?: () => void;
  /** Open, but its teams are still being resolved: rows show a skeleton until they arrive. */
  hydrating?: boolean;
}
export function LivescoreMatchGroup(t0) {
  const $ = _c(64);
  const {
    group,
    entities,
    sport,
    filter,
    oddsMap,
    isMobile,
    isFavourite,
    onToggleFavourite,
    tvByMatch,
    reserveTvSlot: t1,
    collapsed,
    onToggle,
    hydrating: t2
  } = t0;
  const reserveTvSlot = t1 === undefined ? false : t1;
  const hydrating = t2 === undefined ? false : t2;
  const nav = useNavigation();
  const labels = useLabels();
  const ls = labels.livescore;
  let t3;
  if ($[0] !== entities || $[1] !== group.competitionId) {
    t3 = asCompetition(entities, group.competitionId);
    $[0] = entities;
    $[1] = group.competitionId;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const competition = t3;
  const competitionName = competition?.name ?? group.competitionId;
  let t4;
  if ($[3] !== sport) {
    t4 = SPORTS_WITH_COMPETITION_PAGE.has(sport);
    $[3] = sport;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const hasCompetitionPage = t4;
  let t5;
  if ($[5] !== group.competitionId || $[6] !== hasCompetitionPage || $[7] !== nav) {
    t5 = hasCompetitionPage ? nav.standings(group.competitionId) : undefined;
    $[5] = group.competitionId;
    $[6] = hasCompetitionPage;
    $[7] = nav;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const standingsHref = t5;
  const collapsible = collapsed !== undefined && onToggle !== undefined;
  let t6;
  if ($[9] !== group.events) {
    t6 = group.events.filter(_temp);
    $[9] = group.events;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const liveCount = t6.length;
  const t7 = `flex items-center gap-2 px-3 py-2 border-b border-border ${collapsible ? "cursor-pointer select-none" : ""}`;
  const t8 = collapsible ? onToggle : undefined;
  let t9;
  if ($[11] !== competition || $[12] !== competitionName || $[13] !== group.competitionId) {
    t9 = competition?.assets?.logo && <TeamLogo src={competition.assets.logo} alt={competitionName} size="sm" kind="competition" competitionId={group.competitionId} />;
    $[11] = competition;
    $[12] = competitionName;
    $[13] = group.competitionId;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== competitionName || $[16] !== standingsHref) {
    t10 = standingsHref ? <Link href={standingsHref} prefetch={false} onClick={_temp2} className="text-xs font-semibold truncate link-neutral hover:underline">{competitionName}</Link> : <span className="text-xs font-semibold truncate block">{competitionName}</span>;
    $[15] = competitionName;
    $[16] = standingsHref;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== competition) {
    t11 = competition?.country?.name && <span className="text-xs text-muted-foreground ml-1.5">{competition.country.name}</span>;
    $[18] = competition;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10 || $[21] !== t11) {
    t12 = <div className="flex-1 min-w-0">{t10}{t11}</div>;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== collapsed || $[24] !== group.events.length || $[25] !== liveCount || $[26] !== ls) {
    t13 = collapsed && <span className="shrink-0 flex items-center gap-1.5 text-xs text-muted-foreground">{liveCount > 0 && <span className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">{ls.liveCount.replace("{count}", String(liveCount))}</span>}<span>{ls.matchesCount.replace("{count}", String(group.events.length))}</span></span>;
    $[23] = collapsed;
    $[24] = group.events.length;
    $[25] = liveCount;
    $[26] = ls;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== group.competitionId || $[29] !== isFavourite || $[30] !== onToggleFavourite) {
    t14 = onToggleFavourite && <button onClick={e_1 => {
      e_1.stopPropagation();
      onToggleFavourite(group.competitionId);
    }} className="shrink-0 cursor-pointer" aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}><Star className={`w-4 h-4 transition-colors ${isFavourite ? "text-yellow-500" : "text-muted-foreground hover:text-yellow-500"}`} fill={isFavourite ? "currentColor" : "none"} /></button>;
    $[28] = group.competitionId;
    $[29] = isFavourite;
    $[30] = onToggleFavourite;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== collapsed || $[33] !== ls || $[34] !== standingsHref) {
    t15 = standingsHref && !collapsed && <Link href={standingsHref} prefetch={false} onClick={_temp3} className="text-xs link-neutral shrink-0 cursor-pointer hover:underline">{ls.standings}</Link>;
    $[32] = collapsed;
    $[33] = ls;
    $[34] = standingsHref;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== collapsed || $[37] !== collapsible || $[38] !== ls || $[39] !== onToggle) {
    t16 = collapsible && <button type="button" onClick={e_3 => {
      e_3.stopPropagation();
      onToggle();
    }} className="shrink-0 cursor-pointer text-muted-foreground" aria-expanded={!collapsed} aria-label={collapsed ? ls.expandCompetition : ls.collapseCompetition} title={collapsed ? ls.expandCompetition : ls.collapseCompetition}><ChevronDown className={`w-4 h-4 transition-transform ${collapsed ? "" : "rotate-180"}`} aria-hidden={true} /></button>;
    $[36] = collapsed;
    $[37] = collapsible;
    $[38] = ls;
    $[39] = onToggle;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t12 || $[42] !== t13 || $[43] !== t14 || $[44] !== t15 || $[45] !== t16 || $[46] !== t7 || $[47] !== t8 || $[48] !== t9) {
    t17 = <div className={t7} onClick={t8}>{t9}{t12}{t13}{t14}{t15}{t16}</div>;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
    $[46] = t7;
    $[47] = t8;
    $[48] = t9;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== collapsed || $[51] !== entities || $[52] !== filter || $[53] !== group.events || $[54] !== hydrating || $[55] !== isMobile || $[56] !== oddsMap || $[57] !== reserveTvSlot || $[58] !== sport || $[59] !== tvByMatch) {
    t18 = !collapsed && (hydrating ? <div className="divide-y divide-border/50" aria-busy="true">{group.events.map(_temp4)}</div> : <div className="divide-y divide-border/50">{group.events.map(event_0 => <LivescoreMatchRow key={event_0.id} event={event_0} entities={entities} sport={sport} filter={filter} oddsMap={oddsMap} isMobile={isMobile} tvListings={tvByMatch?.[event_0.id]} reserveTvSlot={reserveTvSlot} />)}</div>);
    $[50] = collapsed;
    $[51] = entities;
    $[52] = filter;
    $[53] = group.events;
    $[54] = hydrating;
    $[55] = isMobile;
    $[56] = oddsMap;
    $[57] = reserveTvSlot;
    $[58] = sport;
    $[59] = tvByMatch;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  let t19;
  if ($[61] !== t17 || $[62] !== t18) {
    t19 = <div className="mb-2 bg-card rounded-md border border-border overflow-hidden">{t17}{t18}</div>;
    $[61] = t17;
    $[62] = t18;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  return t19;
}
function _temp4(event) {
  return <div key={event.id} className="h-10 animate-pulse bg-muted/30" />;
}
function _temp3(e_2) {
  return e_2.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp(e) {
  return e.status?.type === LIVE_STATUS_TYPE;
}